import { Auth } from "firebase/auth";
import {
  collection,
  deleteDoc,
  doc,
  Firestore,
  setDoc,
} from "firebase/firestore";
import { JSX, useCallback, useEffect } from "react";
import { useFirestoreCollection, useFirestoreDoc, useUser } from "reactfire";
import { openLoginModal } from "../../components/auth/useOpenLoginModal";
import { createLogger } from "../../utils/logger";
import { UserContext, UserDocument, UserFavorites } from "./UserContext";

const logger = createLogger("UserProvider");

export const UserProvider = ({
  auth,
  firestore,
  children,
}: {
  auth: Auth;
  firestore: Firestore;
  children: JSX.Element;
}) => {
  // Get current Firebase Auth user
  const { data: authUser } = useUser();
  const currentUserId = auth.currentUser?.uid ?? "anonymous";

  // User document reference and data
  const userDocRef = doc(firestore, "users", currentUserId);

  // Get user document (only if user is logged in)
  const userDocQuery = useFirestoreDoc<UserDocument>(userDocRef, {
    suspense: false,
  });
  const user = userDocQuery.data?.data() ?? null;

  // User favorites collection reference
  const userFavoritesCollection = collection(
    firestore,
    "users",
    currentUserId,
    "favorites"
  );

  const { data: userFavoritesSnapshot } = useFirestoreCollection<UserFavorites>(
    userFavoritesCollection
  );

  const userFavorites =
    userFavoritesSnapshot?.docs?.reduce((acc, doc) => {
      acc[doc.id] = doc.data().favoritedAt;
      return acc;
    }, {} as UserFavorites) ?? {};

  const updateFavoriteCampground = useCallback(
    async (campgroundId: string, isFavorite: boolean) => {
      if (!auth.currentUser) {
        openLoginModal();
        return;
      }
      const favoriteDocRef = doc(
        firestore,
        "users",
        auth.currentUser.uid,
        "favorites",
        campgroundId
      );

      if (isFavorite) {
        await setDoc(favoriteDocRef, { favoritedAt: new Date() });
      } else {
        await deleteDoc(favoriteDocRef);
      }
    },
    [firestore, auth]
  );

  const updateUserEmail = useCallback(
    async (email: string) => {
      if (!auth.currentUser) {
        openLoginModal();
        return;
      }

      const userDocRef = doc(firestore, "users", auth.currentUser.uid);
      await setDoc(
        userDocRef,
        {
          email,
          lastUpdated: new Date(),
        },
        { merge: true }
      );
    },
    [firestore, auth]
  );

  // Auto-sync user email when they authenticate
  useEffect(() => {
    if (authUser?.email && user?.email) {
      // Only update if the email is different from what's stored
      if (user?.email !== authUser.email) {
        updateUserEmail(authUser.email).catch((error) => {
          logger.error("Failed to sync user email:", error);
        });
      }
    }
  }, [authUser?.email, user?.email, updateUserEmail, auth.currentUser]);

  const addFavorite = async (campgroundId: string) => {
    await updateFavoriteCampground(campgroundId, true);
  };

  const removeFavorite = async (campgroundId: string) => {
    await updateFavoriteCampground(campgroundId, false);
  };

  const isFavorite = (campgroundId: string) => {
    return userFavorites[campgroundId] !== undefined;
  };

  return (
    <UserContext.Provider
      value={{
        user,
        isAdmin: user?.is_admin === true,
        favorites: userFavorites,
        addFavorite,
        removeFavorite,
        isFavorite,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};
